import { useCallback, useEffect, useRef, useState } from 'react'
import { Check } from 'lucide-react'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { ContentCopyIcon } from '@/components/icons'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

interface CopyButtonProps {
  text: string
  className?: string
  tooltipLabel?: string
  getHtml?: () => string | null | undefined
  'data-testid'?: string
}

function sanitizeHtmlForClipboard(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html')
  doc.body.querySelectorAll('*').forEach((el) => {
    el.removeAttribute('class')
    el.removeAttribute('style')
  })

  return doc.body.innerHTML
}

async function writeToClipboard(text: string, html: string | null | undefined): Promise<void> {
  if (html && typeof ClipboardItem !== 'undefined' && navigator.clipboard.write) {
    try {
      const cleaned = sanitizeHtmlForClipboard(html)
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/plain': new Blob([text], { type: 'text/plain' }),
          'text/html': new Blob([cleaned], { type: 'text/html' }),
        }),
      ])

      return
    } catch {
      // Fall through to writeText for browsers/contexts that reject ClipboardItem.
    }
  }
  await navigator.clipboard.writeText(text)
}

export function CopyButton({
  text,
  className,
  tooltipLabel = 'Copy',
  getHtml,
  'data-testid': dataTestId,
}: Readonly<CopyButtonProps>) {
  const [copied, setCopied] = useState(false)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    }
  }, [])

  const handleCopy = useCallback(async () => {
    try {
      await writeToClipboard(text, getHtml?.())
      setCopied(true)
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
      timeoutRef.current = setTimeout(() => setCopied(false), 2000)
    } catch {
      toast.error('Failed to copy to clipboard')
    }
  }, [text, getHtml])

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={handleCopy}
            aria-label={copied ? 'Copied' : tooltipLabel}
            data-testid={dataTestId}
            className={cn(
              'inline-flex cursor-pointer items-center justify-center rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
              className,
            )}
          >
            {copied ? (
              <Check className="h-4 w-4" />
            ) : (
              <ContentCopyIcon className="h-4 w-4 text-foreground" />
            )}
          </button>
        </TooltipTrigger>
        <TooltipContent
          side="bottom"
          className="border-0 bg-foreground px-2 py-1 text-xs text-background"
        >
          {copied ? 'Copied' : tooltipLabel}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
